import { Icon } from '@iconify/react';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import config from '../../config';
import { useAuth } from '../../context/AuthContext';
import { formatDisplayDate } from '../../utils/formatDate';

/**
 * Latest Registered Mobile App Users (PDF Page 6)
 * Strictly consumer mobile app users only (excludes Venue Owners, Admins, Employees).
 */
const LatestRegisteredOne = ({ className = "col-xxl-8 col-xl-7 col-12" }) => {
    const { auth } = useAuth();
    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(true);

    useEffect(() => {
        const controller = new AbortController();
        const signal = controller.signal;

        const fetchLatestUsers = async () => {
            try {
                const response = await auth.fetch(`${config.API_BASE_URL}/api/users/latest?role=user`, { signal });
                if (response.ok) {
                    const data = await response.json();
                    setUsers(data);
                }
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error('Failed to fetch latest users', error);
                }
            } finally {
                if (!signal.aborted) {
                    setLoadingUsers(false);
                }
            }
        };

        fetchLatestUsers();

        return () => {
            controller.abort();
        };
    }, [auth]);

    return (
        <div className={className}>
            <div className="card h-100">
                <div className="card-body p-24">
                    <div className="d-flex flex-wrap align-items-center gap-1 justify-content-between mb-16">
                        <div className="d-flex align-items-center gap-2">
                            <Icon icon="solar:users-group-two-rounded-bold" className="text-xl text-primary-600" />
                            <h6 className="text-lg fw-semibold mb-0">Latest Registered Mobile App Users</h6>
                        </div>
                        <Link
                            to="/applications/user-management"
                            className="text-primary-600 hover-text-primary d-flex align-items-center gap-1 text-sm fw-medium"
                        >
                            View All
                            <Icon icon="solar:alt-arrow-right-linear" />
                        </Link>
                    </div>

                    <div className="table-responsive scroll-sm">
                        <table className="table bordered-table sm-table mb-0">
                            <thead>
                                <tr>
                                    <th scope="col">User</th>
                                    <th scope="col">Registered On</th>
                                    <th scope="col">Role</th>
                                    <th scope="col" className="text-center">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loadingUsers ? (
                                    <tr><td colSpan="4" className="text-center py-4 text-secondary-light">Loading mobile app users...</td></tr>
                                ) : users.length === 0 ? (
                                    <tr><td colSpan="4" className="text-center py-4 text-secondary-light">No mobile app users registered yet</td></tr>
                                ) : (
                                    users.map(user => (
                                        <tr key={user.id}>
                                            <td>
                                                <div className="d-flex align-items-center">
                                                    <div className="flex-grow-1">
                                                        <h6 className="text-md mb-0 fw-medium">
                                                            {user.full_name || 'Consumer User'}
                                                        </h6>
                                                        <span className="text-sm text-secondary-light fw-medium">
                                                            {user.email || '—'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>{user.created_at ? formatDisplayDate(user.created_at) : '—'}</td>
                                            <td>
                                                <span className="badge bg-primary-50 text-primary-600 border border-primary-100 px-2 py-1 radius-4 text-xs fw-medium">
                                                    Mobile App User
                                                </span>
                                            </td>
                                            <td className="text-center">
                                                <span className={`px-24 py-4 rounded-pill fw-medium text-sm ${user.status === 'active' ? 'bg-success-focus text-success-main' : 'bg-secondary-focus text-secondary-main'}`}>
                                                    {user.status === 'active' ? 'Active' : (user.status || 'Inactive')}
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LatestRegisteredOne;
